import { FundingPolicyType } from '@shared';
import { WaterlilyClient, createQueryKeys } from '@client/api/waterlily';
import type { CarrierSummary, FormListItem } from '@/features/quote-and-apply-builder';

export interface SaveFormParams {
  formDefinition: PolicyFormDefinition;
  formMetadata: PolicyFormMetadata;
  name: string;
  stateCode?: string;
  fundingPolicyTypeId: FundingPolicyType[];
  dryRun?: boolean;
}

export interface SaveFormResult {
  success: boolean;
  formId: string;
  formName: string;
  message: string;
  savedFormConfig?: PolicyFormConfig;
}

export const formBuilderKeys = createQueryKeys('formBuilder');

export interface SendTestEnvelopeParams {
  submissionData: PolicyFormSubmissionData;
  recipientEmail: string;
  recipientName: string;
  roleEmails?: Record<string, string>;
}

export interface SendTestEnvelopeResult {
  envelopeId: string;
  roleKeys: string[];
  recipients: { roleKey: string; email: string }[];
}

export interface TemplateConfigCreateParams {
  insuranceCarrierProductPolicyApplicationFormId: string;
  templates: {
    templateName: string;
    defaultSequence?: number;
    isRequired?: boolean;
    allowedRoles?: string[];
    conditional?: TemplateConditional;
    metadata?: Record<string, any>;
    status?: string;
  }[];
}

export interface TemplateConfigUpdateParams {
  templateName?: string;
  defaultSequence?: number;
  isRequired?: boolean;
  allowedRoles?: string[];
  conditional?: TemplateConditional;
  metadata?: Record<string, any>;
  status?: string;
}

export interface TemplateConfigListResult {
  configs: PolicyApplicationTemplateConfiguration[];
}

export interface TemplateConfigCreateResult {
  configs: PolicyApplicationTemplateConfiguration[];
  errors?: { templateName: string; error: string }[];
}

export interface TemplateConfigUpdateResult {
  config: PolicyApplicationTemplateConfiguration;
}

export interface TemplateConfigDeleteResult {
  success: boolean;
  deletedConfig: PolicyApplicationTemplateConfiguration;
}

export const templateConfigKeys = createQueryKeys('templateConfig');

export interface DocusignTemplateOption {
  templateId: string;
  docusignTemplateId: string;
  name: string;
  description?: string;
  updatedAt: string;
}

export interface DocusignTemplatesListResult {
  templates: DocusignTemplateOption[];
}

export const docusignTemplateKeys = createQueryKeys('docusignTemplates');

export const formBuilderApi = {
  getCarrierSummaries: () =>
    WaterlilyClient.get<CarrierSummary[]>('/form-builder-carrier-summaries'),
  getFormsByCarrierId: (insuranceCarrierId: number) =>
    WaterlilyClient.get<FormListItem[]>(`/form-builder-forms/${insuranceCarrierId}`),
  getFormById: (formId: string) => WaterlilyClient.get<PolicyFormConfig>(`/form/${formId}`),
  saveForm: (params: SaveFormParams) =>
    WaterlilyClient.post<SaveFormResult>('/wizard/build-form-with-config', params),
  deleteForm: (formId: string) => WaterlilyClient.delete<{ success: boolean }>(`/form/${formId}`),
  sendTestEnvelope: (formId: string, params: SendTestEnvelopeParams) =>
    WaterlilyClient.post<SendTestEnvelopeResult>(`/wizard/${formId}/docusign-test-envelope`, params),
  getTemplateConfigs: (formId: string) =>
    WaterlilyClient.get<TemplateConfigListResult>(`/template-config/${formId}`),
  createTemplateConfigs: (params: TemplateConfigCreateParams) =>
    WaterlilyClient.post<TemplateConfigCreateResult>('/template-config', params),
  updateTemplateConfig: (id: string, params: TemplateConfigUpdateParams) =>
    WaterlilyClient.put<TemplateConfigUpdateResult>(`/template-config/${id}`, params),
  deleteTemplateConfig: (id: string) =>
    WaterlilyClient.delete<TemplateConfigDeleteResult>(`/template-config/${id}`),
  getDocusignTemplates: () =>
    WaterlilyClient.get<DocusignTemplatesListResult>('/wizard/docusign-templates'),
};
